import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Form, Input, InputNumber, Modal } from "antd"
import { useTranslation } from "react-i18next"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import styles from "./CreateTicketTypeModal.module.css"
import { spacings } from "themes/variables"

const { TextArea } = Input

export const CreateTicketTypeModal = ({ open, onCancel, onSave, initialValues }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  const isEditing = !!initialValues

  useEffect(() => {
    if (open && initialValues) {
      form.setFieldsValue(initialValues)
    }
  }, [open, initialValues, form])

  const handleSave = () => {
    form.validateFields().then(({ title, description, maxCapacity }) => {
      onSave?.({ title: title.trim(), description: description.trim(), maxCapacity })
      handleCancel()
    })
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel?.()
  }

  const modalTitle = isEditing ? t("experiences.ticket_type.edit_title") : t("experiences.ticket_type.create_title")

  const content = (
    <>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label={t("experiences.ticket_type.title_label")} rules={[{ required: true, message: t("experiences.ticket_type.title_required") }]} className="mb-m">
          <Input placeholder={t("experiences.ticket_type.name_placeholder")} size="large" />
        </Form.Item>

        <Form.Item name="description" label={t("experiences.ticket_type.description_label")} rules={[{ required: true, message: t("experiences.ticket_type.description_required") }]} className="mb-m">
          <TextArea
            placeholder={t("experiences.ticket_type.description_placeholder")}
            rows={4}
            size="large"
          />
        </Form.Item>

        <Form.Item name="maxCapacity" label={t("experiences.ticket_type.max_capacity_label")} rules={[{ required: true, message: t("experiences.ticket_type.max_capacity_required") }, { type: "number", min: 1, message: t("experiences.ticket_type.max_capacity_min") }]} className="mb-m">
          <InputNumber min={1} precision={0} placeholder={t("experiences.ticket_type.max_capacity_placeholder")} size="large" style={{ width: "100%" }} />
        </Form.Item>
      </Form>

      <Flex gap={spacings.S} className={styles.footer}>
        <Button onClick={handleCancel} className={styles.footerBtn}>
          {t("global.cancel")}
        </Button>
        <Button type="primary" onClick={handleSave} className={styles.footerBtn}>
          {isEditing ? t("common.save") : t("experiences.ticket_type.create_button")}
        </Button>
      </Flex>
    </>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => handleCancel()} title={modalTitle}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal open={open} onCancel={handleCancel} title={modalTitle} footer={null} width={540} centered>
      {content}
    </Modal>
  )
}

CreateTicketTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    maxCapacity: PropTypes.number,
  }),
}

CreateTicketTypeModal.defaultProps = {
  initialValues: null,
}
